import { useEffect, useState } from 'react'
import NewVersionBanner from '../../components/NewVersionBanner'
import RefusedChangeBanner from '../../components/RefusedChangeBanner'

import { useT } from '../../i18n'
import './CrewModeShell.css'
import { readCrewPermissionBlocked } from './crewPermissionBlocked'

// Fast Entry v0: the minimal layout for every /crew* route, replacing the
// admin Sidebar + app-header (strategy doc §8 — Crew Mode gets its own
// layout, no inherited admin chrome). The only functional control carried
// over from the admin header's quick-actions menu is sign-out — everything
// else there (team-name edit, admin shortcut links, log export) is
// admin-config-oriented (O-4) and correctly left out.

export type CrewModeShellProps = {
  children: React.ReactNode
  onSignOut: () => void
  /** Present only when the current /crew* route isn't the home screen — renders a back control. Undefined on the home screen itself hides it. */
  onBackToHome?: () => void
}

export default function CrewModeShell({ children, onSignOut, onBackToHome }: CrewModeShellProps) {
  const t = useT()
  const [confirmingSignOut, setConfirmingSignOut] = useState(false)
  const [permissionBlocked, setPermissionBlocked] = useState(false)

  // Findings #123: say why records don't sync when the server refuses them.
  useEffect(() => {
    let cancelled = false
    const check = () => {
      void readCrewPermissionBlocked().then((blocked) => {
        if (!cancelled) setPermissionBlocked(blocked)
      })
    }
    check()
    const timer = window.setInterval(check, 5000)
    window.addEventListener('focus', check)
    window.addEventListener('online', check)
    return () => {
      cancelled = true
      window.clearInterval(timer)
      window.removeEventListener('focus', check)
      window.removeEventListener('online', check)
    }
  }, [])

  return (
    <div className="crew-shell">
      <header className="crew-shell-bar">
        {onBackToHome && (
          <button
            type="button"
            data-testid="crew-shell-back"
            className="crew-shell-btn crew-shell-back"
            aria-label={t('crewMode.shell.backToMenu')}
            onClick={onBackToHome}
          >
            ‹
          </button>
        )}
        <span className="crew-shell-mark" aria-hidden="true">S</span>
        <span className="crew-shell-title">{t('nav.crewMode')}</span>
        {confirmingSignOut ? (
          <div className="crew-shell-confirm" role="group" aria-label={t('crewMode.shell.confirmSignOut')}>
            <span className="crew-shell-confirm-text">{t('crewMode.shell.confirmSignOut')}</span>
            <button
              type="button"
              data-testid="crew-shell-signout-confirm"
              className="crew-shell-btn crew-shell-btn-danger"
              onClick={onSignOut}
            >
              {t('app.signOut')}
            </button>
            <button
              type="button"
              data-testid="crew-shell-signout-cancel"
              className="crew-shell-btn"
              onClick={() => setConfirmingSignOut(false)}
            >
              {t('common.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="crew-shell-signout"
            className="crew-shell-btn"
            onClick={() => setConfirmingSignOut(true)}
          >
            {t('app.signOut')}
          </button>
        )}
      </header>
      <NewVersionBanner compact />
      {permissionBlocked && (
        <div
          role="alert"
          data-testid="crew-shell-permission-blocked"
          style={{ margin: '8px 12px 0', padding: '10px 12px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontWeight: 700, fontSize: 14 }}
        >
          {t('crewMode.shell.permissionBlocked')}
        </div>
      )}
      <RefusedChangeBanner variant="crew" />
      <main className="crew-shell-content">{children}</main>
    </div>
  )
}
